/*
 * collectionTypes.js — Collection Type Registry (Task A).
 *
 * Defines the canonical list of collection types supported by the app.
 * Consumed in later tasks for:
 *   - Field templates              (Task B → fills `templateFields`)
 *   - Category templates           (Task C → fills `templateCategories`)
 *   - Analytics profile mapping    (Task D → fills `analyticsProfile`)
 *   - Monetisation / plan locks    (Task E → reads `plan`)
 *   - Collection creation flow     (chooses a type at create time)
 *   - Dashboard scope mapping      (selected collection → analytics view)
 *
 * This file is INTENTIONALLY data-only. No JSX, no API calls, no side
 * effects. It does NOT touch any existing collection records, Dashboard
 * logic, or UI. Consumers will import the registry / helpers when those
 * downstream tasks land.
 *
 * Schema (per entry):
 *   id                  unique string id (kebab/lowercase)
 *   name                human-readable label shown in UI later
 *   description         short summary of what users track in this type
 *   plan                "basic" | "premium" — monetisation tier
 *   icon                placeholder string identifier (real icon mapping
 *                       happens in Task E / UI work; freeform string today)
 *   enabled             boolean — toggles the type on/off without deleting
 *   templateFields      array — filled in Task B (default: [])
 *   templateCategories  array — filled in Task C (default: [])
 *   analyticsProfile    string — filled in Task D (default: "")
 */

export const COLLECTION_TYPES = [
  // ---------- Core types (Basic plan) ----------
  {
    id: "wine",
    name: "Wine Collection",
    description: "Track bottles, regions, grapes, vintages, and drinking windows.",
    plan: "basic",
    icon: "wine",
    enabled: true,
    templateFields: [
      { id: "producer",   label: "Producer",        type: "text",     required: false },
      { id: "wineName",   label: "Wine Name",       type: "text",     required: false },
      { id: "vintage",    label: "Vintage",         type: "number",   required: false },
      { id: "country",    label: "Country",         type: "text",     required: false },
      { id: "region",     label: "Region",          type: "text",     required: false },
      { id: "subRegion",  label: "Sub-Region",      type: "text",     required: false },
      { id: "grape",      label: "Grape Variety",   type: "text",     required: false },
      { id: "abv",        label: "ABV %",           type: "number",   required: false },
      { id: "bottleSize", label: "Bottle Size",     type: "text",     required: false },
      { id: "drinkFrom",  label: "Drink From",      type: "number",   required: false },
      { id: "drinkTo",    label: "Drink To",        type: "number",   required: false },
      { id: "rating",     label: "Rating",          type: "number",   required: false },
      { id: "notes",      label: "Tasting Notes",   type: "textarea", required: false },
      { id: "quantity",   label: "Quantity Owned",  type: "number",   required: false },
    ],
    templateCategories: [],
    analyticsProfile: "",
  },
  {
    id: "whisky",
    name: "Whisky Collection",
    description: "Track distilleries, regions, age statements, casks, and bottlings.",
    plan: "basic",
    icon: "whisky",
    enabled: true,
    templateFields: [
      { id: "distillery",   label: "Distillery",    type: "text",   required: false },
      { id: "region",       label: "Region",        type: "text",   required: false },
      { id: "age",          label: "Age Statement", type: "number", required: false },
      { id: "caskType",     label: "Cask Type",     type: "text",   required: false },
      { id: "bottlingYear", label: "Bottling Year", type: "number", required: false },
    ],
    templateCategories: [],
    analyticsProfile: "",
  },
  {
    id: "spirits",
    name: "Spirits Collection",
    description: "Track general spirits — brand, style, ABV, vintage, and provenance.",
    plan: "basic",
    icon: "spirits",
    enabled: true,
    templateFields: [
      { id: "distillery", label: "Distillery", type: "text",   required: false },
      { id: "type",       label: "Type",       type: "text",   required: false },
      { id: "abv",        label: "ABV %",      type: "number", required: false },
      { id: "age",        label: "Age",        type: "number", required: false },
      { id: "caskType",   label: "Cask Type",  type: "text",   required: false },
    ],
    templateCategories: [],
    analyticsProfile: "",
  },
];

/**
 * getCollectionType(typeId) — returns the registry entry for the given id,
 * or `undefined` if the id is unknown. Consumers should treat `undefined`
 * as "no template" (current free-form behaviour).
 */
export function getCollectionType(typeId) {
  if (!typeId) return undefined;
  return COLLECTION_TYPES.find((t) => t.id === typeId);
}

/**
 * Convenience: list all enabled types, optionally filtered by plan.
 * Not strictly required by Task A but harmless to expose for downstream
 * tasks (e.g. the collection creation flow in a later task).
 */
export function listCollectionTypes({ plan } = {}) {
  return COLLECTION_TYPES.filter(
    (t) => t.enabled && (plan ? t.plan === plan : true)
  );
}
